(() => {
  if (window.mocloomGalleryInitialized) return;
  window.mocloomGalleryInitialized = true;
  const cleanups = new Map();
  const gallerySelector = 'product-info[id^="MainProduct"] [id^="GalleryViewer-"]';

  const initGallery = (gallery) => {
    if (!gallery || gallery.dataset.mocloomAligned === 'true') return;

    const slider = gallery.querySelector('[id^="Slider-Gallery-"]');
    const prevButton = gallery.querySelector('.slider-button--prev');
    const nextButton = gallery.querySelector('.slider-button--next');
    const getThumbnails = () => Array.from(gallery.querySelectorAll('[data-product-gallery-thumb]'));
    const track = gallery.querySelector('.product-gallery-thumbnails');
    const pagination = gallery.querySelector('[data-gallery-pagination]');
    const controls = prevButton?.parentElement;
    const abort = new AbortController();
    const options = { signal: abort.signal };
    let activeId;
    let dotsKey;

    if (!slider || !prevButton || !nextButton) return;

    gallery.dataset.mocloomAligned = 'true';
    const mobile = window.matchMedia('(max-width: 749px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const getSlides = () =>
      Array.from(slider.querySelectorAll('[id^="Slide-"]')).filter(
        (slide) => slide.clientWidth > 0
      );

    const updateTail = () => {
      slider.style.setProperty(
        '--moc-product-gallery-tail',
        `${slider.clientWidth}px`
      );
    };

    const nearestIndex = () => {
      const slides = getSlides();
      if (!slides.length) return 0;
      return slides.reduce((closest, slide, index) => {
        const currentDistance = Math.abs(slider.scrollLeft - centeredScrollLeft(slide));
        const closestDistance = Math.abs(
          slider.scrollLeft - centeredScrollLeft(slides[closest])
        );
        return currentDistance < closestDistance ? index : closest;
      }, 0);
    };

    const slideIndexFromThumb = (thumb) => {
      const targetId = thumb && thumb.dataset ? thumb.dataset.target : null;
      const slides = getSlides();
      if (!targetId || !slides.length) return -1;
      return slides.findIndex((slide) => slide.id === targetId);
    };

    const thumbIndexFromSlide = (slide) => {
      if (!slide || !slide.id) return -1;
      return getThumbnails().findIndex((thumb) => thumb.dataset.target === slide.id);
    };

    const centeredScrollLeft = (slide) => mobile.matches
      ? slide.offsetLeft - (slider.clientWidth - slide.clientWidth) / 2
      : slide.offsetLeft;

    const updateSlideStates = (forcedIndex = nearestIndex()) => {
      const slides = getSlides();

      if (!mobile.matches) return;

      slides.forEach((slide, index) => {
        const distance = (centeredScrollLeft(slide) - slider.scrollLeft) / slide.clientWidth;
        const depth = Math.min(1, Math.abs(distance));
        slide.style.setProperty('--card-shift', `${-Math.max(-1, Math.min(1, distance)) * 28}%`);
        slide.style.setProperty('--card-scale', 1 - depth * 0.08);
        slide.style.setProperty('--card-brightness', 1 - depth * 0.35);
        slide.style.setProperty('--card-layer', String(100 - Math.round(depth * 50)));
        slide.classList.toggle('is-active', index === forcedIndex);
        slide.classList.toggle('is-previous', index === forcedIndex - 1);
        slide.classList.toggle('is-next', index === forcedIndex + 1);
      });
    };

    const alignTo = (index) => {
      const slides = getSlides();
      if (!slides.length) return;
      const targetIndex = Math.max(0, Math.min(index, slides.length - 1));
      slider.scrollTo({
        left: centeredScrollLeft(slides[targetIndex]),
        behavior: reducedMotion.matches ? 'instant' : 'smooth',
      });
    };

    const updateActiveThumb = (forcedIndex = thumbIndexFromSlide(getSlides()[nearestIndex()])) => {
      const thumbnails = getThumbnails();
      const slides = getSlides();
      const current = nearestIndex();
      thumbnails.forEach((thumb, index) => {
        const isActive = index === forcedIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (mobile.matches) {
          thumb.removeAttribute('aria-selected');
          thumb.setAttribute('aria-current', String(isActive));
          thumb.hidden = !slides.some((slide) => slide.id === thumb.dataset.target);
        } else {
          thumb.hidden = false;
          thumb.removeAttribute('aria-current');
        }
      });
      if (!mobile.matches) {
        track?.setAttribute('role', 'tablist');
        if (pagination) pagination.hidden = true;
        controls.hidden = false;
        controls.removeAttribute('data-single-media');
        return;
      }
      track?.setAttribute('role', 'group');
      controls.hidden = slides.length === 0;
      controls.toggleAttribute('data-single-media', slides.length === 1);
      prevButton.disabled = current === 0;
      nextButton.disabled = current >= slides.length - 1;
      const selected = thumbnails[forcedIndex];
      if (selected && track && activeId !== selected.dataset.target) {
        activeId = selected.dataset.target;
        const left = track.scrollLeft + selected.getBoundingClientRect().left - track.getBoundingClientRect().left - (track.clientWidth - selected.offsetWidth) / 2;
        track.scrollTo({ left: Math.max(0, Math.min(left, track.scrollWidth - track.clientWidth)), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      }
      if (!pagination) return;
      pagination.hidden = slides.length < 2;
      const start = Math.max(0, Math.min(current - 2, slides.length - 5));
      const windowSlides = slides.slice(start, start + 5);
      const key = windowSlides.map((slide) => slide.id).join('|');
      const dots = pagination.querySelector('[data-gallery-dots]');
      if (key !== dotsKey) {
        dotsKey = key;
        const focusedId = dots.contains(document.activeElement) ? document.activeElement.dataset.target : null;
        dots.replaceChildren(...windowSlides.map((slide, offset) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.target = slide.id;
          button.setAttribute('aria-label', `Show media ${start + offset + 1}`);
          return button;
        }));
        if (focusedId) [...dots.children].find((button) => button.dataset.target === focusedId)?.focus({ preventScroll: true });
      }
      [...dots.children].forEach((button) => button.setAttribute('aria-current', String(button.dataset.target === slides[current]?.id)));
      const count = pagination.querySelector('[data-gallery-count]');
      const value = `${slides.length ? current + 1 : 0} / ${slides.length}`;
      if (count.textContent !== value) count.textContent = value;
    };

    const onButtonClick = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const direction = event.currentTarget.name === 'next' ? 1 : -1;
      alignTo(nearestIndex() + direction);
    };

    const onMediaClick = (event) => {
      if (!mobile.matches || event.detail === 0) return;

      const slide = event.target.closest('[id^="Slide-"]');
      if (!slide || (!slide.classList.contains('is-previous') && !slide.classList.contains('is-next'))) return;

      event.preventDefault();
      event.stopPropagation();
      alignTo(getSlides().indexOf(slide));
    };

    // Thumbnail click → scroll to corresponding slide
    gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-product-gallery-thumb], [data-gallery-dots] button');
        if (!thumb) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const targetIndex = slideIndexFromThumb(thumb);
        if (targetIndex >= 0) alignTo(targetIndex);
    }, { capture: true, signal: abort.signal });

    let scrollFrame = null;
    const scheduleThumbUpdate = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        updateSlideStates();
        updateActiveThumb();
        scrollFrame = null;
      });
    };

    updateTail();
    updateSlideStates();
    updateActiveThumb();
    prevButton.addEventListener('click', onButtonClick, { capture: true, ...options });
    nextButton.addEventListener('click', onButtonClick, { capture: true, ...options });
    slider.addEventListener('click', onMediaClick, { capture: true, ...options });
    slider.addEventListener('scroll', scheduleThumbUpdate, options);
    gallery.addEventListener('slideChanged', scheduleThumbUpdate, options);
    const refresh = () => { activeId = null; updateTail(); scheduleThumbUpdate(); };
    window.addEventListener('resize', refresh, options);
    const observer = new MutationObserver(() => {
      getSlides().forEach((slide) => resizeObserver.observe(slide));
      refresh();
    });
    observer.observe(slider, { childList: true });
    const resizeObserver = new ResizeObserver(refresh);
    resizeObserver.observe(slider);
    getSlides().forEach((slide) => resizeObserver.observe(slide));
    cleanups.set(gallery, () => {
      abort.abort(); observer.disconnect(); resizeObserver.disconnect();
      window.cancelAnimationFrame(scrollFrame);
      delete gallery.dataset.mocloomAligned;
    });
  };

  const initAll = () => {
    cleanups.forEach((cleanup, gallery) => {
      if (!gallery.isConnected) { cleanup(); cleanups.delete(gallery); }
    });
    document.querySelectorAll(gallerySelector).forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', (event) => {
    cleanups.forEach((cleanup, gallery) => {
      if (event.target.contains(gallery)) { cleanup(); cleanups.delete(gallery); }
    });
  });
})();
